import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  NgZone,
  OnDestroy,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { AcmicService } from'@mapfre-design-library/src/public_api';

@Component({
  standalone: true,
  imports: [CommonModule],
  selector: 'app-info-tooltip',
  templateUrl: './info-tooltip.component.html',
  styleUrls: ['./info-tooltip.component.css'],
})
export class InfoTooltipComponent implements OnDestroy {
  @Input() content!: string;
  @Input() position: 'top' | 'bottom' = 'top';
  @Input() align: 'left' | 'right' | 'center' = 'center';
  @Input() edgeOffset = 0;

  @ViewChild('trigger', { static: true }) triggerEl!: ElementRef<HTMLElement>;
  @ViewChild('bubble') bubbleEl?: ElementRef<HTMLElement>;

  visible = false;
  isAcmic = false
  bubbleLeft = '';
  arrowLeft = '50%';
  private resizeListener = () => { if (this.visible) this.adjustPosition(); };
  private showTimeout?: any;

  constructor(
    private host: ElementRef<HTMLElement>,
    private acmicService: AcmicService,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef
  ) {
    this.isAcmic = this.acmicService.isACMIC();
    this.ngZone.runOutsideAngular(() => {
      window.addEventListener('resize', this.resizeListener);
    });
  }

  @HostListener('document:click')
  onDocumentClick() {
    this.hide();
  }

  show() {
    this.visible = true;
    this.showTimeout = setTimeout(() => this.adjustPosition());
  }

  hide() {
    this.visible = false;
  }

  toggle() {
    if(!this.visible)
      this.show();
    else
      this.hide();
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.resizeListener);
    if (this.showTimeout) clearTimeout(this.showTimeout);
  }

  private adjustPosition(): void {
    if (!this.bubbleEl) return;

    const CONTAINER_PADDING = 24;
    const ARROW_W = 23;

    const bubble: HTMLElement = this.bubbleEl.nativeElement;
    const trigger: HTMLElement = this.triggerEl.nativeElement;
    const host: HTMLElement = this.host.nativeElement;

    const bubbleWidth = bubble.offsetWidth;
    const triggerRect = trigger.getBoundingClientRect();
    const hostRect = host.getBoundingClientRect();

    const container = this.findContainer(host);
    const containerRect = container.getBoundingClientRect();
    const containerPaddingLeft  = parseFloat(getComputedStyle(container).paddingLeft)  || CONTAINER_PADDING;
    const containerPaddingRight = parseFloat(getComputedStyle(container).paddingRight) || CONTAINER_PADDING;

    const innerLeft  = containerRect.left  + containerPaddingLeft;
    const innerRight = containerRect.right - containerPaddingRight;

    const iconCentreVp = triggerRect.left + triggerRect.width / 2;

    let bubbleLeftPx = iconCentreVp - hostRect.left - bubbleWidth / 2;

    const minBubbleLeft = innerLeft  - hostRect.left;
    const maxBubbleLeft = innerRight - hostRect.left - bubbleWidth;

    switch (this.align) {
      case 'left':
        bubbleLeftPx = innerLeft - hostRect.left + +this.edgeOffset;
        break;
      case 'right':
        bubbleLeftPx = innerRight - hostRect.left - bubbleWidth - +this.edgeOffset;
        break;
      default:
        bubbleLeftPx = Math.max(minBubbleLeft, Math.min(bubbleLeftPx, maxBubbleLeft));
    }

    const arrowLeftPx = iconCentreVp - hostRect.left - bubbleLeftPx - ARROW_W / 2;
    const clampedArrowLeft = Math.max(
      3,
      Math.min(arrowLeftPx, bubbleWidth - 3 - ARROW_W)
    );

    this.bubbleLeft = `${bubbleLeftPx}px`;
    this.arrowLeft = `${clampedArrowLeft}px`;
    this.ngZone.run(() => this.cdr.detectChanges());
  }

  private findContainer(el: HTMLElement): HTMLElement {
    const SELECTORS = [
      '.billing-new-container', '.mapfre-card-container'
    ];
    for (const sel of SELECTORS) {
      const found = el.closest(sel) as HTMLElement | null;
      if (found) return found;
    }
    let cur = el.parentElement;
    while (cur && cur !== document.body) {
      const style = getComputedStyle(cur);
      if (style.overflow === 'hidden' || style.overflowX === 'hidden' ||
          style.display === 'block' || style.display === 'flex') {
        return cur;
      }
      cur = cur.parentElement;
    }
    return document.body;
  }
}
